import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  SelectChangeEvent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
  useTheme,
  Card,
  CardHeader,
  CardContent,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HotelIcon from '@mui/icons-material/Hotel';
import ConstructionIcon from '@mui/icons-material/Construction';
import ApartmentIcon from '@mui/icons-material/Apartment';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import dayjs from 'dayjs';
import { api } from '@services';
import { Hotel, HotelFormData, HotelFormProps } from '@types';

const HotelForm: React.FC<HotelFormProps> = ({ mode = 'create', initialData, onSuccess }) => {
  const [formData, setFormData] = useState<HotelFormData>({
    name: initialData?.name || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    state: initialData?.state || '',
    zip_code: initialData?.zip_code || '',
    country: initialData?.country || 'USA',
    num_rooms: initialData?.num_rooms || 0,
    star_rating: initialData?.star_rating || 0,
    year_built: initialData?.year_built || 0,
    lot_size: initialData?.lot_size || 0,
    property_type: initialData?.property_type || '',
    chain_affiliation: initialData?.chain_affiliation || '',
    amenities: initialData?.amenities || [],
    renovation_history: initialData?.renovation_history || [],
    description: initialData?.description || '',
    website: initialData?.website || '',
    notes: initialData?.notes || '',

    // Owner information from existing fields
    owner_name: initialData?.owner_name || '',
    owner_type: initialData?.owner_type || '',
    owner_entity: initialData?.owner_entity || '',
    owner_address: initialData?.owner_address || '',
    owner_city: initialData?.owner_city || '',
    owner_state: initialData?.owner_state || '',
    owner_zip: initialData?.owner_zip || '',
    owner_country: initialData?.owner_country || 'USA',
    owner_contact_name: initialData?.owner_contact_name || '',
    owner_contact_email: initialData?.owner_contact_email || '',
    owner_contact_phone: initialData?.owner_contact_phone || '',
    owner_since: initialData?.owner_since || '',
    ownership_structure: initialData?.ownership_structure || '',
    ownership_notes: initialData?.ownership_notes || '',

    // New owner fields from database update
    ownership_percentage: initialData?.ownership_percentage || 100,
    acquisition_date: initialData?.acquisition_date || '',
    ownership_type_details: initialData?.ownership_type_details || '',
    tax_id: initialData?.tax_id || '',
    legal_entity_type: initialData?.legal_entity_type || '',

    // Keep backward compatibility with owner_info
    owner_info: initialData?.owner_info || {
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      type: '',
      company: '',
    },
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  const propertyTypes = ['Full Service', 'Limited Service', 'Extended Stay', 'Resort', 'Boutique', 'Conference Center', 'Casino', 'Mixed Use'];

  const commonAmenities = ['Restaurant', 'Pool', 'Fitness Center', 'Business Center', 'Meeting Rooms', 'Spa', 'Room Service', 'Parking', 'Airport Shuttle', 'Pet Friendly'];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Map owner_info fields to direct owner fields for backward compatibility
      const formattedData = {
        ...formData,
        // Only set these if the direct fields are empty but owner_info has values
        owner_name: formData.owner_name || formData.owner_info.name,
        owner_contact_email: formData.owner_contact_email || formData.owner_info.email,
        owner_contact_phone: formData.owner_contact_phone || formData.owner_info.phone,
        owner_address: formData.owner_address || formData.owner_info.address,
        owner_city: formData.owner_city || formData.owner_info.city,
        owner_state: formData.owner_state || formData.owner_info.state,
        owner_zip: formData.owner_zip || formData.owner_info.zip_code,
        owner_country: formData.owner_country || formData.owner_info.country,
        owner_type: formData.owner_type || formData.owner_info.type,
        owner_entity: formData.owner_entity || formData.owner_info.company,
      };

      if (mode === 'create') {
        const response = await api.post('/hotels', formattedData);
        setSuccess(`Hotel "${response.data.name}" added successfully!`);
      } else {
        const response = await api.put(`/hotels/${initialData?.id}`, formattedData);
        setSuccess(`Hotel "${response.data.name}" updated successfully!`);
      }

      setTimeout(() => {
        onSuccess?.();
        navigate('/hotels');
      }, 1500);
    } catch (error: any) {
      setError(error.response?.data?.message || `Failed to ${mode} hotel`);
    } finally {
      setLoading(false);
    }
  };

  const handleOwnerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Extract the field name without the owner_ prefix
    const field = name.replace('owner_', '');
    setFormData({
      ...formData,
      owner_info: {
        ...formData.owner_info,
        [field]: value,
      },
    });
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? 0 : Number(value),
    }));
  };

  const handleRenovationChange = (index: number, field: string, value: string | number) => {
    setFormData((prev) => {
      const updatedHistory = [...prev.renovation_history];
      updatedHistory[index] = {
        ...updatedHistory[index],
        [field]: typeof value === 'number' ? value : Number(value) || 0,
      };
      return {
        ...prev,
        renovation_history: updatedHistory,
      };
    });
  };

  const handleAddRenovation = () => {
    setFormData((prev) => ({
      ...prev,
      renovation_history: [...prev.renovation_history, { year: new Date().getFullYear(), description: '', cost: 0 }],
    }));
  };

  const handleRemoveRenovation = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      renovation_history: prev.renovation_history.filter((_, i) => i !== index),
    }));
  };

  const handleAmenitiesChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      amenities: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleOwnerDateChange = (field: string, date: dayjs.Dayjs | null) => {
    if (date) {
      setFormData((prev) => ({
        ...prev,
        [field]: date.format('YYYY-MM-DD'),
      }));
    }
  };

  const legalEntityTypes = [
    'Corporation',
    'Limited Liability Company (LLC)',
    'Partnership',
    'Limited Partnership (LP)',
    'Sole Proprietorship',
    'Trust',
    'Real Estate Investment Trust (REIT)',
    'Joint Venture',
    'Non-Profit Organization',
    'Other'
  ];

  const ownershipStructures = [
    'Single Owner',
    'Joint Ownership',
    'Partnership',
    'Investment Group',
    'Family Trust',
    'Corporate Entity',
    'REIT',
    'Private Equity',
    'Institutional Investor',
    'Foreign Investor',
    'Other'
  ];

  return (
    <Box sx={{
      py: 5,
      px: { xs: 2, md: 0 },
      minHeight: '100vh',
      background: theme => `linear-gradient(to bottom, ${alpha(theme.palette.background.default, 0.9)}, ${alpha(theme.palette.background.paper, 0.3)})`,
    }} className="fade-in">
      <Container maxWidth="md">
        <Paper
          elevation={2}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            mb: 4,
            boxShadow: '0 8px 40px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: theme => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {mode === 'create' ? 'Add New Hotel' : 'Edit Hotel Details'}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/hotels')}
              sx={{
                borderRadius: 8,
                px: 2,
                '&:hover': {
                  transform: 'translateX(-4px)'
                }
              }}
            >
              Back to Hotels
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {/* Basic Hotel Information Card */}
              <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'visible' }}>
                <CardHeader
                  avatar={
                    <BusinessIcon color="primary" sx={{ fontSize: 28 }} />
                  }
                  title={
                    <Typography variant="h6" fontWeight={600}>
                      Basic Information
                    </Typography>
                  }
                  sx={{
                    bgcolor: theme => alpha(theme.palette.primary.light, 0.1),
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Hotel Name"
                        name="name"
                        value={formData.name}
                        onChange={handleTextChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <HotelIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Property Type</InputLabel>
                        <Select
                          name="property_type"
                          value={formData.property_type}
                          onChange={(e) => handleTextChange(e as any)}
                          label="Property Type"
                          startAdornment={
                            <InputAdornment position="start">
                              <ApartmentIcon color="action" />
                            </InputAdornment>
                          }
                        >
                          {propertyTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Chain Affiliation"
                        name="chain_affiliation"
                        value={formData.chain_affiliation}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Number of Rooms"
                        name="num_rooms"
                        value={formData.num_rooms || ''}
                        onChange={handleNumberChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Star Rating"
                        name="star_rating"
                        value={formData.star_rating || ''}
                        onChange={handleNumberChange}
                        inputProps={{ min: 0, max: 5, step: 0.5 }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Year Built"
                        name="year_built"
                        value={formData.year_built || ''}
                        onChange={handleNumberChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Lot Size (sq ft)"
                        name="lot_size"
                        value={formData.lot_size || ''}
                        onChange={handleNumberChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        name="description"
                        value={formData.description}
                        onChange={handleTextChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <DescriptionIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Website"
                        name="website"
                        value={formData.website}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Amenities</InputLabel>
                        <Select
                          multiple
                          value={formData.amenities}
                          onChange={handleAmenitiesChange}
                          input={<OutlinedInput label="Amenities" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(selected as string[]).map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  size="small"
                                  sx={{
                                    borderRadius: '16px',
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.1)
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {commonAmenities.map((amenity) => (
                            <MenuItem key={amenity} value={amenity}>
                              {amenity}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Location Information Card */}
              <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'visible' }}>
                <CardHeader
                  avatar={
                    <LocationOnIcon color="secondary" sx={{ fontSize: 28 }} />
                  }
                  title={
                    <Typography variant="h6" fontWeight={600}>
                      Location Information
                    </Typography>
                  }
                  sx={{
                    bgcolor: theme => alpha(theme.palette.secondary.light, 0.1),
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        name="address"
                        value={formData.address}
                        onChange={handleTextChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOnIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="City"
                        name="city"
                        value={formData.city}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="State"
                        name="state"
                        value={formData.state}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="ZIP Code"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Country"
                        name="country"
                        value={formData.country}
                        onChange={handleTextChange}
                        InputProps={{
                          defaultValue: 'USA'
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Renovation History Card */}
              <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'visible' }}>
                <CardHeader
                  avatar={
                    <ConstructionIcon color="warning" sx={{ fontSize: 28 }} />
                  }
                  title={
                    <Typography variant="h6" fontWeight={600}>
                      Renovation History
                    </Typography>
                  }
                  action={
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddRenovation}
                      color="warning"
                      size="small"
                      sx={{
                        borderRadius: 8,
                        mr: 1
                      }}
                    >
                      Add
                    </Button>
                  }
                  sx={{
                    bgcolor: theme => alpha(theme.palette.warning.light, 0.1),
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <CardContent>
                  <Box sx={{ mb: 2 }}>
                    {formData.renovation_history.length === 0 ? (
                      <Alert
                        severity="info"
                        icon={<InfoIcon />}
                        sx={{ borderRadius: 2 }}
                      >
                        No renovation history added yet. Click "Add" to record past renovations.
                      </Alert>
                    ) : (
                      formData.renovation_history.map((renovation, index) => (
                        <Paper
                          key={index}
                          sx={{
                            p: 2,
                            mb: 2,
                            bgcolor: 'background.default',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: theme => alpha(theme.palette.divider, 0.1),
                            transition: 'all 0.2s',
                            '&:hover': {
                              boxShadow: 2,
                              borderColor: theme => alpha(theme.palette.primary.main, 0.2),
                            }
                          }}
                        >
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                label="Year"
                                type="number"
                                value={renovation.year}
                                onChange={(e) => handleRenovationChange(index, 'year', e.target.value)}
                                InputProps={{ inputProps: { min: 1900, max: new Date().getFullYear() } }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                              <TextField
                                fullWidth
                                label="Description"
                                type="text"
                                name="description"
                                value={renovation.description}
                                onChange={(e) => handleRenovationChange(index, 'description', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <TextField
                                fullWidth
                                label="Cost"
                                type="number"
                                value={renovation.cost}
                                onChange={(e) => handleRenovationChange(index, 'cost', e.target.value)}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                  inputProps: { min: 0 },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={1}>
                              <IconButton
                                onClick={() => handleRemoveRenovation(index)}
                                color="error"
                                sx={{
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.error.main, 0.1),
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Additional Information Card */}
              <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'visible' }}>
                <CardHeader
                  avatar={
                    <InfoIcon color="info" sx={{ fontSize: 28 }} />
                  }
                  title={
                    <Typography variant="h6" fontWeight={600}>
                      Additional Information
                    </Typography>
                  }
                  sx={{
                    bgcolor: theme => alpha(theme.palette.info.light, 0.1),
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <CardContent>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Additional Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleTextChange}
                    placeholder="Enter any other important information about this property..."
                  />
                </CardContent>
              </Card>

              {/* Owner Information Card */}
              <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'visible' }}>
                <CardHeader
                  avatar={
                    <PersonIcon color="success" sx={{ fontSize: 28 }} />
                  }
                  title={
                    <Typography variant="h6" fontWeight={600}>
                      Owner Information
                    </Typography>
                  }
                  sx={{
                    bgcolor: theme => alpha(theme.palette.success.light, 0.1),
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Owner Name"
                        name="owner_name"
                        value={formData.owner_name}
                        onChange={handleTextChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Owner Entity/Company"
                        name="owner_entity"
                        value={formData.owner_entity}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Legal Entity Type</InputLabel>
                        <Select
                          name="legal_entity_type"
                          value={formData.legal_entity_type}
                          onChange={(e) => handleTextChange(e as any)}
                          label="Legal Entity Type"
                        >
                          {legalEntityTypes.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Ownership Structure</InputLabel>
                        <Select
                          name="ownership_structure"
                          value={formData.ownership_structure}
                          onChange={(e) => handleTextChange(e as any)}
                          label="Ownership Structure"
                        >
                          {ownershipStructures.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider>
                        <Chip
                          label="Ownership Details"
                          variant="filled"
                          color="success"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Divider>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Tax ID"
                        name="tax_id"
                        value={formData.tax_id}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Owner Type"
                        name="owner_type"
                        value={formData.owner_type}
                        onChange={handleTextChange}
                        placeholder="Individual, Company, etc."
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Ownership Percentage"
                        name="ownership_percentage"
                        type="number"
                        value={formData.ownership_percentage || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          ownership_percentage: parseFloat(e.target.value) || 0
                        })}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          inputProps: { min: 0, max: 100, step: 0.01 }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Owner Since"
                          value={formData.owner_since ? dayjs(formData.owner_since) : null}
                          onChange={(date) => handleOwnerDateChange('owner_since', date)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: "outlined"
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Acquisition Date"
                          value={formData.acquisition_date ? dayjs(formData.acquisition_date) : null}
                          onChange={(date) => handleOwnerDateChange('acquisition_date', date)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              variant: "outlined"
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider>
                        <Chip
                          label="Contact Information"
                          variant="filled"
                          color="success"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Divider>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Contact Person"
                        name="owner_contact_name"
                        value={formData.owner_contact_name}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Contact Email"
                        name="owner_contact_email"
                        type="email"
                        value={formData.owner_contact_email}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Contact Phone"
                        name="owner_contact_phone"
                        value={formData.owner_contact_phone}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Divider>
                        <Chip
                          label="Address"
                          variant="filled"
                          color="success"
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Divider>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Owner Address"
                        name="owner_address"
                        value={formData.owner_address}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Owner City"
                        name="owner_city"
                        value={formData.owner_city}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Owner State/Province"
                        name="owner_state"
                        value={formData.owner_state}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Owner ZIP/Postal Code"
                        name="owner_zip"
                        value={formData.owner_zip}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        fullWidth
                        label="Owner Country"
                        name="owner_country"
                        value={formData.owner_country}
                        onChange={handleTextChange}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Ownership Type Details"
                        name="ownership_type_details"
                        value={formData.ownership_type_details}
                        onChange={handleTextChange}
                        placeholder="Additional details about the ownership structure"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Ownership Notes"
                        name="ownership_notes"
                        value={formData.ownership_notes}
                        onChange={handleTextChange}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                loading={loading}
                sx={{
                  height: 56,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  background: theme => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                    background: theme => `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                  }
                }}
              >
                {mode === 'create' ? 'Add Hotel' : 'Save Changes'}
              </LoadingButton>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default HotelForm;
